import { createSlice, ListenerMiddlewareInstance, PayloadAction } from '@reduxjs/toolkit';
import { difference, union } from 'es-toolkit/array';
import { WritableDraft } from 'immer';
import type { RootState } from '../../../configuration/setup/store';
import {
    GetDepartedVehiclesQueryParams,
    GetIncomingVehiclesQueryParams,
    GetInventoryVehiclesFilterQueryParams,
    GetOutgoingVehiclesFilterQueryParams,
    matchDepartedVehiclesReceived,
    matchIncomingVehiclesReceived,
    matchInventoryVehiclesReceived,
    matchOutgoingVehiclesReceived,
} from '../api/compoundManagementApi';
import {
    CompoundManagementView,
    CompoundVehicle,
    LoadPreviewArrivals,
    LoadPreviewDepartures,
    LoadPreviewUnannouncedDepartures,
} from '../compoundManagement/compoundManagement.types';
import { DepartedVehiclesTableColumn } from '../compoundManagement/departed/table/DepartedVehiclesTableColumn';
import { IncomingVehiclesTableColumn } from '../compoundManagement/incoming/table/IncomingVehiclesTableColumn';
import { VehicleInventoryTableColumn } from '../compoundManagement/inventory/table/VehicleInventoryTableColumn';
import { OutgoingVehiclesTableColumn } from '../compoundManagement/outgoing/table/OutgoingVehiclesTableColumn';
import { MeansOfTransportType, VehicleType } from '../sharedComponents/common.types';
import { SortDirection, TableSort } from '../sharedComponents/table/SortedTableHeader';
import { parseUniqueEntries } from '../sharedComponents/inputListParser';
import { createDebouncedValue } from '../../../configuration/setup/debouncedStateUpdate';
import { DEBOUNCE_TIME } from '../debounceConfig';

export const NO_SELECTED_COMPOUND_VEHICLE = undefined;

type Sorting = {
    incoming: TableSort<IncomingVehiclesTableColumn>;
    inventory: TableSort<VehicleInventoryTableColumn>;
    outgoing: TableSort<OutgoingVehiclesTableColumn>;
    departed: TableSort<DepartedVehiclesTableColumn>;
};

type BulkSelection = {
    incoming: string[];
    inventory: string[];
    outgoing: string[];
};

export enum BulkStatusUpdateStep {
    BULK_SELECTION = 'inactive',
    VALIDATION = 'validation',
    REVIEW_TABLE = 'table',
    FORM = 'form',
}

export type LoadPreview =
    | ({ view: CompoundManagementView.INCOMING_VEHICLES } & LoadPreviewArrivals)
    | ({ view: CompoundManagementView.INVENTORY } & LoadPreviewUnannouncedDepartures)
    | ({ view: CompoundManagementView.OUTGOING_VEHICLES } & LoadPreviewDepartures)
    | undefined;

export enum BulkStatusUpdateErrorCode {
    DUPLICATE_VIN = 'duplicateVin',
}

type BulkStatusUpdateState = {
    step: BulkStatusUpdateStep;
    loadPreview: LoadPreview;
    errorCode: BulkStatusUpdateErrorCode | undefined;
};

export type CompoundManagementModalFilter = {
    locationFilter: string | undefined;
    vehicleTypeFilter: VehicleType | undefined;
    manufacturerFilter: string | undefined;
    arrivalDateStartAtFilter: Date | undefined;
    arrivalDateEndAtFilter: Date | undefined;
    departureDateStartAtFilter: Date | undefined;
    departureDateEndAtFilter: Date | undefined;
    loadIdFilter: string | undefined;
    meansOfTransportIdFilter: string | undefined;
    finalDestinationFilter: string | undefined;
    arrivalMeansOfTransportTypeFilter: MeansOfTransportType | undefined;
    departureMeansOfTransportTypeFilter: MeansOfTransportType | undefined;
    arrivalFromFilter: string | undefined;
    departureToFilter: string | undefined;
    excludeCanceledFilter: boolean;
};

export type CompoundManagementState = {
    activeCompoundManagementView: CompoundManagementView;
    selectedVehicle: CompoundVehicle | undefined;
    sorting: Sorting;
    compoundManagementModalFilter: CompoundManagementModalFilter;
    vinSearchFilter: string;
    debouncedVinSearchFilter: string;
    bulkSelection: BulkSelection;
    bulkStatusUpdate: BulkStatusUpdateState;
};

const initialCompoundManagementState: CompoundManagementState = {
    activeCompoundManagementView: CompoundManagementView.INVENTORY,
    selectedVehicle: NO_SELECTED_COMPOUND_VEHICLE,
    sorting: {
        incoming: { dir: SortDirection.ASCENDING, column: IncomingVehiclesTableColumn.EXPECTED_ARRIVAL_DATE },
        inventory: { dir: SortDirection.DESCENDING, column: VehicleInventoryTableColumn.ARRIVAL_DATE },
        outgoing: { dir: SortDirection.ASCENDING, column: OutgoingVehiclesTableColumn.EXPECTED_DEPARTURE_DATE },
        departed: { dir: SortDirection.DESCENDING, column: DepartedVehiclesTableColumn.DEPARTURE_DATE },
    },
    compoundManagementModalFilter: {
        locationFilter: undefined,
        vehicleTypeFilter: undefined,
        manufacturerFilter: undefined,
        arrivalDateStartAtFilter: undefined,
        arrivalDateEndAtFilter: undefined,
        departureDateStartAtFilter: undefined,
        departureDateEndAtFilter: undefined,
        loadIdFilter: undefined,
        meansOfTransportIdFilter: undefined,
        finalDestinationFilter: undefined,
        arrivalMeansOfTransportTypeFilter: undefined,
        departureMeansOfTransportTypeFilter: undefined,
        arrivalFromFilter: undefined,
        departureToFilter: undefined,
        excludeCanceledFilter: false,
    },
    vinSearchFilter: '',
    debouncedVinSearchFilter: '',
    bulkSelection: { incoming: [], inventory: [], outgoing: [] },
    bulkStatusUpdate: { step: BulkStatusUpdateStep.BULK_SELECTION, loadPreview: undefined, errorCode: undefined },
};

const debouncedSearchSetup = createDebouncedValue<string>({
    typePrefix: 'compoundManagement/vinSearchFilter',
    debounceTime: DEBOUNCE_TIME,
});

export const compoundManagementSlice = createSlice({
    name: 'compoundManagement',
    initialState: initialCompoundManagementState,
    reducers: {
        switchView: (state: CompoundManagementState, action: PayloadAction<CompoundManagementView>) => {
            const sameView = state.activeCompoundManagementView === action.payload;

            state.activeCompoundManagementView = action.payload;
            if (!sameView) {
                state.selectedVehicle = NO_SELECTED_COMPOUND_VEHICLE;
            }
        },
        setIncomingSorting: (
            state: CompoundManagementState,
            action: PayloadAction<TableSort<IncomingVehiclesTableColumn>>,
        ) => {
            state.sorting.incoming = action.payload;
        },
        setInventorySorting: (
            state: CompoundManagementState,
            action: PayloadAction<TableSort<VehicleInventoryTableColumn>>,
        ) => {
            state.sorting.inventory = action.payload;
        },
        setOutgoingSorting: (
            state: CompoundManagementState,
            action: PayloadAction<TableSort<OutgoingVehiclesTableColumn>>,
        ) => {
            state.sorting.outgoing = action.payload;
        },
        setDepartedSorting: (
            state: CompoundManagementState,
            action: PayloadAction<TableSort<DepartedVehiclesTableColumn>>,
        ) => {
            state.sorting.departed = action.payload;
        },
        setCompoundManagementModalFilter: (
            state: CompoundManagementState,
            action: PayloadAction<CompoundManagementModalFilter>,
        ) => {
            state.compoundManagementModalFilter = action.payload;
        },
        selectCompoundVehicle: (state: CompoundManagementState, action: PayloadAction<CompoundVehicle>) => {
            state.selectedVehicle = action.payload;
        },
        deselectCompoundVehicle: (state: CompoundManagementState) => {
            state.selectedVehicle = NO_SELECTED_COMPOUND_VEHICLE;
        },
        setLoadPreview: (state: CompoundManagementState, action: PayloadAction<LoadPreview>) => {
            state.bulkStatusUpdate.loadPreview = action.payload;
        },
        setInitialState: () => initialCompoundManagementState,
        addToIncomingBulkSelection: (state: CompoundManagementState, action: PayloadAction<string[]>) => {
            state.bulkSelection.incoming = union(state.bulkSelection.incoming, action.payload);
        },
        addToInventoryBulkSelection: (state: CompoundManagementState, action: PayloadAction<string[]>) => {
            state.bulkSelection.inventory = union(state.bulkSelection.inventory, action.payload);
        },
        addToOutgoingBulkSelection: (state: CompoundManagementState, action: PayloadAction<string[]>) => {
            state.bulkSelection.outgoing = union(state.bulkSelection.outgoing, action.payload);
        },
        removeFromIncomingBulkSelection: (state: CompoundManagementState, action: PayloadAction<string[]>) => {
            state.bulkSelection.incoming = difference(state.bulkSelection.incoming, action.payload);
        },
        removeFromInventoryBulkSelection: (state: CompoundManagementState, action: PayloadAction<string[]>) => {
            state.bulkSelection.inventory = difference(state.bulkSelection.inventory, action.payload);
        },
        removeFromOutgoingBulkSelection: (state: CompoundManagementState, action: PayloadAction<string[]>) => {
            state.bulkSelection.outgoing = difference(state.bulkSelection.outgoing, action.payload);
        },
        clearIncomingBulkSelection: (state: CompoundManagementState) => {
            state.bulkSelection.incoming = [];
        },
        clearInventoryBulkSelection: (state: CompoundManagementState) => {
            state.bulkSelection.inventory = [];
        },
        clearOutgoingBulkSelection: (state: CompoundManagementState) => {
            state.bulkSelection.outgoing = [];
        },
        startBulkStatusUpdateWithEmptySelection: (state: CompoundManagementState) => {
            state.bulkStatusUpdate.step = BulkStatusUpdateStep.REVIEW_TABLE;
            state.bulkStatusUpdate.loadPreview = undefined;
            state.bulkStatusUpdate.errorCode = undefined;
        },
        startBulkStatusUpdate: (state: CompoundManagementState, action: PayloadAction<LoadPreview>) => {
            state.bulkStatusUpdate.step = BulkStatusUpdateStep.VALIDATION;
            state.bulkStatusUpdate.loadPreview = action.payload;
            state.bulkStatusUpdate.errorCode = undefined;
        },
        startBulkStatusUpdateWithError: (
            state: CompoundManagementState,
            action: PayloadAction<BulkStatusUpdateErrorCode>,
        ) => {
            state.bulkStatusUpdate.step = BulkStatusUpdateStep.VALIDATION;
            state.bulkStatusUpdate.loadPreview = undefined;
            state.bulkStatusUpdate.errorCode = action.payload;
        },
        nextBulkStatusUpdateStep: (state: CompoundManagementState) => {
            if (state.bulkStatusUpdate.errorCode !== undefined) {
                return;
            }

            if (state.bulkStatusUpdate.loadPreview) {
                state.bulkStatusUpdate.loadPreview = { ...state.bulkStatusUpdate.loadPreview, invalidVins: [] };
            }

            switch (state.bulkStatusUpdate.step) {
                case BulkStatusUpdateStep.VALIDATION:
                    state.bulkStatusUpdate.step = BulkStatusUpdateStep.REVIEW_TABLE;
                    return;
                case BulkStatusUpdateStep.REVIEW_TABLE:
                    state.bulkStatusUpdate.step = BulkStatusUpdateStep.FORM;
                    return;
            }
        },
        abortBulkStatusUpdate: (state: CompoundManagementState) => {
            state.bulkStatusUpdate.step = BulkStatusUpdateStep.BULK_SELECTION;
            state.bulkStatusUpdate.loadPreview = undefined;
            state.bulkStatusUpdate.errorCode = undefined;
        },
        completeBulkStatusUpdate: (state: CompoundManagementState) => {
            state.bulkStatusUpdate.step = BulkStatusUpdateStep.BULK_SELECTION;
            state.bulkStatusUpdate.loadPreview = undefined;
            state.bulkStatusUpdate.errorCode = undefined;

            switch (state.activeCompoundManagementView) {
                case CompoundManagementView.INCOMING_VEHICLES:
                    state.bulkSelection.incoming = [];
                    return;
                case CompoundManagementView.INVENTORY:
                    state.bulkSelection.inventory = [];
                    return;
                case CompoundManagementView.OUTGOING_VEHICLES:
                    state.bulkSelection.outgoing = [];
                    return;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(debouncedSearchSetup.setValueAction, (state, action) => {
                state.vinSearchFilter = action.payload.value;
            })
            .addCase(debouncedSearchSetup.setDebouncedValueAction, (state, action) => {
                state.debouncedVinSearchFilter = action.payload;
            })
            .addMatcher(matchIncomingVehiclesReceived, (state, action) => {
                if (state.activeCompoundManagementView === CompoundManagementView.INCOMING_VEHICLES) {
                    resetSelectedVehicle(state, action);
                }
            })
            .addMatcher(matchOutgoingVehiclesReceived, (state, action) => {
                if (state.activeCompoundManagementView === CompoundManagementView.OUTGOING_VEHICLES) {
                    resetSelectedVehicle(state, action);
                }
            })
            .addMatcher(matchInventoryVehiclesReceived, (state, action) => {
                if (state.activeCompoundManagementView === CompoundManagementView.INVENTORY) {
                    resetSelectedVehicle(state, action);
                }
            })
            .addMatcher(matchDepartedVehiclesReceived, (state, action) => {
                if (state.activeCompoundManagementView === CompoundManagementView.DEPARTED) {
                    resetSelectedVehicle(state, action);
                }
            });
    },
});

const resetSelectedVehicle = <Vehicle extends { id: string }>(
    state: WritableDraft<CompoundManagementState>,
    action: PayloadAction<{ items: Vehicle[]; hasMore: boolean }>,
) => {
    const vehicles = action.payload.items;
    const selectedVehicleId = state.selectedVehicle?.id;
    if (selectedVehicleId !== undefined && !vehicles.find((vehicle) => vehicle.id === selectedVehicleId)) {
        state.selectedVehicle = NO_SELECTED_COMPOUND_VEHICLE;
    }
};

const getCompoundManagementState = (state: RootState) => state.portal.compoundManagement;

export const getActiveCompoundManagementView = (state: RootState) =>
    getCompoundManagementState(state).activeCompoundManagementView;

export const getCompoundManagementModalFilter = (state: RootState) =>
    getCompoundManagementState(state).compoundManagementModalFilter;

export const getVinSearchFilter = (state: RootState) => getCompoundManagementState(state).vinSearchFilter;
export const getDebouncedVinSearchFilter = (state: RootState) =>
    getCompoundManagementState(state).debouncedVinSearchFilter;

export const getSelectedCompoundVehicle = (state: RootState) => getCompoundManagementState(state).selectedVehicle;

export const getIncomingVehicleSorting = (state: RootState) => getCompoundManagementState(state).sorting.incoming;
export const getOutgoingVehicleSorting = (state: RootState) => getCompoundManagementState(state).sorting.outgoing;
export const getVehicleInventorySorting = (state: RootState) => getCompoundManagementState(state).sorting.inventory;
export const getDepartedVehiclesSorting = (state: RootState) => getCompoundManagementState(state).sorting.departed;

export const getIncomingVehicleBulkSelection = (state: RootState) =>
    getCompoundManagementState(state).bulkSelection.incoming;
export const getVehicleInventoryBulkSelection = (state: RootState) =>
    getCompoundManagementState(state).bulkSelection.inventory;
export const getOutgoingVehicleBulkSelection = (state: RootState) =>
    getCompoundManagementState(state).bulkSelection.outgoing;
export const getLoadPreview = (state: RootState) => getCompoundManagementState(state).bulkStatusUpdate.loadPreview;

export const getBulkStatusUpdateStep = (state: RootState) => getCompoundManagementState(state).bulkStatusUpdate.step;
export const getBulkStatusUpdateErrorCode = (state: RootState) =>
    getCompoundManagementState(state).bulkStatusUpdate.errorCode;

export const setVinSearchFilter = debouncedSearchSetup.setValueAction;

export const addSliceListeners = (listenerMiddleware: ListenerMiddlewareInstance) => {
    listenerMiddleware.startListening(debouncedSearchSetup.listener);
};

export const mapIncomingVehiclesFilterQueryParams = (
    filters: CompoundManagementModalFilter,
    vinSearch: string,
): GetIncomingVehiclesQueryParams => ({
    location: filters.locationFilter,
    vehicleType: filters.vehicleTypeFilter,
    manufacturer: filters.manufacturerFilter,
    expectedArrivalDateStartAt: filters.arrivalDateStartAtFilter,
    expectedArrivalDateEndAt: filters.arrivalDateEndAtFilter,
    loadId: filters.loadIdFilter,
    meansOfTransportId: filters.meansOfTransportIdFilter,
    finalDestination: filters.finalDestinationFilter,
    vins: mapVinSearchToFilter(vinSearch),
    excludeCancelled: filters.excludeCanceledFilter,
    arrivalMeansOfTransportType: filters.arrivalMeansOfTransportTypeFilter,
    arrivalFromLocation: filters.arrivalFromFilter,
});

export const mapInventoryVehiclesFilterQueryParams = (
    filters: CompoundManagementModalFilter,
    vinSearch: string,
): GetInventoryVehiclesFilterQueryParams => ({
    location: filters.locationFilter,
    vehicleType: filters.vehicleTypeFilter,
    manufacturer: filters.manufacturerFilter,
    arrivalDateStartAt: filters.arrivalDateStartAtFilter,
    arrivalDateEndAt: filters.arrivalDateEndAtFilter,
    finalDestination: filters.finalDestinationFilter,
    vins: mapVinSearchToFilter(vinSearch),
    excludeCancelled: filters.excludeCanceledFilter,
    arrivalMeansOfTransportType: filters.arrivalMeansOfTransportTypeFilter,
    arrivalFromLocation: filters.arrivalFromFilter,
});

export const mapOutgoingVehiclesFilterQueryParams = (
    filters: CompoundManagementModalFilter,
    vinSearch: string,
): GetOutgoingVehiclesFilterQueryParams => ({
    location: filters.locationFilter,
    vehicleType: filters.vehicleTypeFilter,
    manufacturer: filters.manufacturerFilter,
    expectedDepartureDateStartAt: filters.departureDateStartAtFilter,
    expectedDepartureDateEndAt: filters.departureDateEndAtFilter,
    finalDestination: filters.finalDestinationFilter,
    loadId: filters.loadIdFilter,
    meansOfTransportId: filters.meansOfTransportIdFilter,
    vins: mapVinSearchToFilter(vinSearch),
    excludeCancelled: filters.excludeCanceledFilter,
    departureMeansOfTransportType: filters.departureMeansOfTransportTypeFilter,
    departureToLocation: filters.departureToFilter,
});

export const mapDepartedVehiclesFilterQueryParams = (
    filters: CompoundManagementModalFilter,
    vinSearch: string,
): GetDepartedVehiclesQueryParams => ({
    location: filters.locationFilter,
    vehicleType: filters.vehicleTypeFilter,
    manufacturer: filters.manufacturerFilter,
    arrivalDateStartAt: filters.arrivalDateStartAtFilter,
    arrivalDateEndAt: filters.arrivalDateEndAtFilter,
    departureDateStartAt: filters.departureDateStartAtFilter,
    departureDateEndAt: filters.departureDateEndAtFilter,
    finalDestination: filters.finalDestinationFilter,
    vins: mapVinSearchToFilter(vinSearch),
    arrivalMeansOfTransportType: filters.arrivalMeansOfTransportTypeFilter,
    departureMeansOfTransportType: filters.departureMeansOfTransportTypeFilter,
    arrivalFromLocation: filters.arrivalFromFilter,
    departureToLocation: filters.departureToFilter,
    excludeCancelled: filters.excludeCanceledFilter,
});

const mapVinSearchToFilter = (vinSearch: string): string[] | undefined => {
    const vins = parseUniqueEntries(vinSearch);
    return vins.length > 0 ? vins : undefined;
};
